import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchStudentsAsync } from '../store/slices/studentsSlice';
import axios from 'axios';
import LoadingSpinner from './LoadingSpinner';
import { formatDateEuropean } from '../utils/dateUtils';
import { getApiErrorMessage } from '../utils/apiError';
import './Students.css';
import './GroupForm.css';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

interface SubscriptionRow {
  id: number;
  studentId: number;
  startDate: string;
  paymentDate?: string;
  lessonsCount: number;
  pricePerLesson: string;
  endDate?: string | null;
  estimatedEndDate?: string | null;
  effectiveEndDate?: string | null;
  totalAmount?: string;
  revenue?: string;
  subscriptionType?: { id: number; name: string };
  // Group is the student's CURRENT group (subscriptions are student-scoped).
  student?: {
    id: number;
    firstName: string;
    lastName: string;
    surname?: string;
    groups?: Array<{ id: number; name?: string; level: string }>;
  };
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

const Subscriptions: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => state.auth?.token);
  const { students } = useAppSelector((state) => state.students);

  const [subscriptions, setSubscriptions] = useState<SubscriptionRow[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 50 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filterStudentId, setFilterStudentId] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Load students for filter dropdown
  useEffect(() => {
    if (students.length === 0) {
      dispatch(fetchStudentsAsync({ page: 1, limit: 500 }));
    }
  }, [dispatch, students.length]);

  const fetchSubscriptions = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '50' });
      if (filterStudentId) params.append('studentId', filterStudentId);
      if (filterDateFrom) params.append('dateFrom', filterDateFrom);
      if (filterDateTo) params.append('dateTo', filterDateTo);

      const response = await axios.get(`${API_BASE_URL}/subscriptions?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setSubscriptions(response.data.subscriptions);
      setPagination(response.data.pagination);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to fetch subscriptions'));
    } finally {
      setLoading(false);
    }
  }, [token, filterStudentId, filterDateFrom, filterDateTo]);

  useEffect(() => {
    void Promise.resolve().then(() => fetchSubscriptions(1));
  }, [fetchSubscriptions]);

  const handleSearch = () => {
    fetchSubscriptions(1);
  };

  const handlePageChange = (newPage: number) => {
    fetchSubscriptions(newPage);
  };

  const handleClearFilters = () => {
    setFilterStudentId('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const studentName = (s?: { firstName: string; lastName: string; surname?: string }) => {
    if (!s) return '—';
    return `${s.firstName} ${s.lastName}${s.surname ? ' ' + s.surname : ''}`;
  };

  const subscriptionEffectiveEnd = (sub: SubscriptionRow) =>
    sub.effectiveEndDate ?? sub.endDate ?? sub.estimatedEndDate;

  const currentGroupName = (sub: SubscriptionRow) => {
    const g = sub.student?.groups && sub.student.groups.length ? sub.student.groups[0] : null;
    return g ? (g.name || g.level?.toUpperCase() || '—') : '—';
  };

  const hasFilters = !!(filterStudentId || filterDateFrom || filterDateTo);

  return (
    <div className="students-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Subscriptions</h1>
          <p>Manage student subscriptions</p>
        </div>
        <button onClick={() => navigate('/subscriptions/create')} className="btn-primary">
          Add Subscription
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      {/* Filters */}
      <div className="filter-bar">
        <select
          value={filterStudentId}
          onChange={(e) => setFilterStudentId(e.target.value)}
          className="form-select"
        >
          <option value="">All Students</option>
          {students.map(s => (
            <option key={s.id} value={s.id}>
              {s.firstName} {s.lastName}{s.surname ? ' ' + s.surname : ''}
            </option>
          ))}
        </select>

        <input
          type="date"
          value={filterDateFrom}
          onChange={(e) => setFilterDateFrom(e.target.value)}
          className="form-input"
          placeholder="From"
        />

        <input
          type="date"
          value={filterDateTo}
          onChange={(e) => setFilterDateTo(e.target.value)}
          className="form-input"
          placeholder="To"
        />

        <button onClick={handleSearch} className="btn-primary">
          Search
        </button>

        <button onClick={handleClearFilters} className="btn-secondary">
          Clear
        </button>
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        <p>
          Showing {subscriptions.length} of {pagination.totalItems} subscription{pagination.totalItems !== 1 ? 's' : ''}
          {hasFilters && ' (filtered)'}
        </p>
      </div>

      {/* Table */}
      <div className="students-table-container">
        {loading ? (
          <LoadingSpinner />
        ) : subscriptions.length === 0 ? (
          <div className="no-students">
            <p>No subscriptions found.</p>
            {hasFilters && (
              <button onClick={handleClearFilters} className="btn-primary">
                Clear filters to see all subscriptions
              </button>
            )}
          </div>
        ) : (
          <table className="students-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Group</th>
                <th>Type</th>
                <th>Start</th>
                <th>Payment</th>
                <th>Lessons</th>
                <th>Price</th>
                <th>Revenue</th>
                <th>End</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((sub) => {
                const end = subscriptionEffectiveEnd(sub);
                return (
                <tr key={sub.id}>
                  <td><strong>{studentName(sub.student)}</strong></td>
                  <td>{currentGroupName(sub)}</td>
                  <td>{sub.subscriptionType?.name || '—'}</td>
                  <td>{formatDateEuropean(sub.startDate)}</td>
                  <td>{sub.paymentDate ? formatDateEuropean(sub.paymentDate) : '—'}</td>
                  <td>{sub.lessonsCount}</td>
                  <td>{parseFloat(sub.pricePerLesson).toFixed(2)}</td>
                  <td>{sub.revenue || sub.totalAmount || '—'} BYN</td>
                  <td>{end ? formatDateEuropean(end) : '—'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        type="button"
                        className="btn-icon"
                        data-tooltip="Edit subscription"
                        onClick={() => navigate(`/subscriptions/edit/${sub.studentId}/${sub.id}`)}
                      >
                        ✎
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn-secondary"
            disabled={pagination.currentPage <= 1}
            onClick={() => handlePageChange(pagination.currentPage - 1)}
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {pagination.currentPage} of {pagination.totalPages}
          </span>
          <button
            className="btn-secondary"
            disabled={pagination.currentPage >= pagination.totalPages}
            onClick={() => handlePageChange(pagination.currentPage + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Subscriptions;
